import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, OnChanges, Output, SimpleChanges, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactItem } from '../../../features/pcheck/models/pcheck.model';

/** Threshold above which the list becomes collapsible with a search box. */
const COLLAPSIBLE_THRESHOLD = 6;

@Component({
  selector: 'app-contact-picker',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './contact-picker.component.html',
  styleUrls: ['./contact-picker.component.scss'],
})
export class ContactPickerComponent implements OnChanges {
  /** Full list of contacts to display. */
  @Input() contacts: ContactItem[] = [];
  /** Disables all buttons when true (e.g. after a selection has been made). */
  @Input() disabled = false;
  /** Label shown above the list. */
  @Input() label = 'Select a contact:';

  /** Emitted when the user clicks a contact card. */
  @Output() contactSelected = new EventEmitter<ContactItem>();

  readonly searchQuery  = signal('');
  // Expanded by default: when the list is collapsible we still show the search
  // box and the contact grid right away rather than hiding them behind a toggle.
  readonly isCollapsed  = signal(false);

  readonly isCollapsible = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    // Only reset search / collapse state when the contacts list itself changes.
    // Toggling `disabled` must not clear the user's current search.
    if (changes['contacts']) {
      this.isCollapsible.set(this.contacts.length > COLLAPSIBLE_THRESHOLD);
      this.searchQuery.set('');
      this.isCollapsed.set(false);
    }
  }

  get filteredContacts(): ContactItem[] {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return this.contacts;
    return this.contacts.filter(c =>
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.title?.toLowerCase().includes(q),
    );
  }

  onSearch(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  toggleCollapse(): void {
    this.isCollapsed.update(v => !v);
  }

  onSelect(contact: ContactItem): void {
    if (!this.disabled) this.contactSelected.emit(contact);
  }

  displayName(c: ContactItem): string {
    return `${c.first_name} ${c.last_name}`.trim() || c.email;
  }
}
